import cx from 'classnames';
import { FC, KeyboardEvent, MouseEvent, useCallback, useMemo } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button } from '@shared/components/button-new';
import { IconButton } from '@shared/components/icon-button';
import { TooltipDelay } from '@shared/components/tooltip';
import { TruncatedTooltip } from '@shared/components/truncated-tooltip';
import { CloseIcon } from '@shared/icons/close';
import { XCircleIcon } from '@shared/icons/x-circle';
import { ButtonSize, NewButtonColour } from '@shared/types/common/button';
import { NotificationStatus, ResolvedNotification } from '@shared/types/notifications';

import { NotificationTypeIcon } from '../notification-type-icon';
import { styles } from './NotificationItem.styles';
import { formatRelativeTime } from '../../../../Notifications.utils';

export interface NotificationItemProps extends WithStyles<typeof styles> {
  notification: ResolvedNotification;
  onAction: (item: ResolvedNotification) => void;
  onClick: (item: ResolvedNotification) => void;
  onDismiss: (item: ResolvedNotification) => void;
  onRetry: (item: ResolvedNotification) => void;
}

// Not memoised by the caller: formatRelativeTime reads the current time, so the
// label must recompute on each render (e.g. the 60s poll) rather than freeze.
const getTimeLabel = (notification: ResolvedNotification) => {
  if (notification.clearingInDays != null) {
    return notification.clearingInDays === 1
      ? t('clears_in_1_day')
      : t('clears_in_xdaysx_days', { days: notification.clearingInDays });
  }

  return formatRelativeTime(notification.createdAt);
};

const NotificationItemComponent: FC<NotificationItemProps> = ({
  classes,
  notification,
  onAction,
  onClick,
  onDismiss,
  onRetry,
}) => {
  const { status, type } = notification;

  const timeLabel = getTimeLabel(notification);

  const isActioned = status === NotificationStatus.Actioned;
  // Link rows (the only type in production today) stay clickable once actioned — seen
  // is not the same as gone, and the row is the only way back to the thread/listing.
  const isLinkRow = type === 'link' && (status === NotificationStatus.Default || isActioned);
  // Informational (link) rows can be dismissed, and so can actioned rows — the success
  // bar is otherwise a dead end with no way to clear it. Action-required rows must still
  // be acted on, and pending rows resolve themselves. An explicit `dismissible` flag
  // overrides the type default. The inner parentheses matter: JS rejects ?? beside ||.
  const dismissible =
    (notification.dismissible ?? (type === 'link' || status === NotificationStatus.Actioned)) &&
    status !== NotificationStatus.Pending;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (isLinkRow && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(notification);
    }
  };

  const withStop = useCallback(
    (fn: (item: ResolvedNotification) => void) => (event: MouseEvent) => {
      event.stopPropagation();
      fn(notification);
    },
    [notification]
  );

  const actionArea = useMemo(() => {
    if (status === NotificationStatus.Pending) {
      return (
        <div className={classes.actions}>
          <span className={classes.pendingButton}>
            <span className={classes.spinner} />
            {t('accepting_ellipsis')}
          </span>
        </div>
      );
    }

    if (status === NotificationStatus.Error) {
      return (
        <div className={classes.statusBar}>
          <span className={classes.statusLeft}>
            <span className={classes.statusIconError}>
              <XCircleIcon color="currentColor" />
            </span>
            <span className={classes.statusText}>{t('couldnt_accept')}</span>
          </span>
          <Button colour={NewButtonColour.Link} size={ButtonSize.small} onClick={withStop(onRetry)}>
            {t('try_again')}
          </Button>
        </div>
      );
    }

    // Once actioned, single/double-action rows drop their buttons and fall through to the
    // muted title/message further down — same treatment as an actioned link row.
    if (type === 'single-action' && !isActioned) {
      return (
        <div className={classes.actions}>
          <Button colour={NewButtonColour.PrimaryNavy} size={ButtonSize.small} onClick={withStop(onAction)}>
            {t(notification.cta?.label ?? 'confirm')}
          </Button>
        </div>
      );
    }

    if (type === 'double-action' && !isActioned) {
      return (
        <div className={classes.actions}>
          <Button colour={NewButtonColour.Outline} size={ButtonSize.small} onClick={withStop(onDismiss)}>
            {t('decline')}
          </Button>
          <Button colour={NewButtonColour.PrimaryNavy} size={ButtonSize.small} onClick={withStop(onAction)}>
            {t('accept')}
          </Button>
        </div>
      );
    }

    if (type === 'link' && notification.cta) {
      // A single call to action, styled as a button rather than a text link: the row has
      // exactly one thing to do and the button makes that unmissable. Once actioned, the
      // button stays at full strength rather than disappearing behind a status bar — it's
      // still the way back into the thread/listing, so it stays as prominent as before.
      return (
        <div className={classes.actions}>
          <Button
            colour={isActioned ? NewButtonColour.Outline : NewButtonColour.PrimaryNavy}
            size={ButtonSize.small}
            onClick={withStop(onClick)}
          >
            {t(notification.cta.label)}
          </Button>
        </div>
      );
    }

    return null;
  }, [classes, isActioned, notification, onAction, onClick, onDismiss, onRetry, status, type, withStop]);

  return (
    <div
      className={cx(classes.root, { [classes.clickable]: isLinkRow })}
      role={isLinkRow ? 'button' : undefined}
      tabIndex={isLinkRow ? 0 : undefined}
      onClick={isLinkRow ? () => onClick(notification) : undefined}
      onKeyDown={handleKeyDown}
    >
      <NotificationTypeIcon muted={isActioned} type={notification.notificationType} />
      <div className={classes.main}>
        <div className={cx(classes.text, { [classes.textActioned]: isActioned })}>
          <TruncatedTooltip
            classes={{ root: classes.title }}
            delay={TooltipDelay.Delayed}
            placement="top"
            text={notification.title}
          />
          <span className={classes.message}>{notification.message}</span>
          {notification.subtext && <span className={classes.subtext}>{notification.subtext}</span>}
          <span className={classes.time}>{timeLabel}</span>
        </div>
        {actionArea}
      </div>
      {dismissible && (
        <IconButton
          aria-label={t('dismiss')}
          classes={{ root: classes.dismiss }}
          colour={NewButtonColour.Ghost}
          icon={<CloseIcon color="currentColor" />}
          size={ButtonSize.iconSmall}
          onClick={withStop(onDismiss)}
        />
      )}
    </div>
  );
};

export const NotificationItem = withStyles(styles)(NotificationItemComponent);
